/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { useEffect, useRef, useState, useCallback } from 'react'
import dayjs from 'dayjs'
import colors from '@sportninja/common/constants/appColors'
import { connect } from 'react-redux'
import { Spin, FloatButton } from 'antd'
import { ArrowUpOutlined } from '@ant-design/icons'
import {
  getGameStatusTypesBySportId,
  getAllGameTypes,
} from '@sportninja/common/selectors/types'
import req from '@sportninja/common/api/request'

import { Flex } from '../../Layout'
import DropdownArrowIcon from '../../icons/DropdownArrowIcon'
import { media, Mobile } from '../../Responsive'
import ListStatusView from '../StatusView'
import useGetScheduleFiltering from '../useGetScheduleFiltering'
import GameListRowV2 from './game-list-row'
import GameListFilters from './GameListFilters'
import MobileGameListRow from './MobileGameListRow'
import { useUrlManager } from '../../../hooks/useUrlManager'
import Text from 'src/components/ts-components/Text'
import TimePeriodFilter from 'src/pages/MySchedule/components/TimePeriodFilter'

const SCROLL_THRESHOLD = 500

export const DayOfGames = ({
  date,
  games = [],
  gameStatusTypes,
  gameTypes,
  isMobile,
  isSoccer = false,
  isHockey = false,
  onClickFirstGame = () => {},
  scheduleId = null,
  isMySchedule = false,
}) => {
  const [isOpen, setIsOpen] = useState(true)

  return (
    <div
      css={css`
        ${isOpen ? 'margin-bottom: 16px;' : ''}

        ${media.mobile} {
          padding: 0 8px;
        }
      `}
    >
      <Flex
        onClick={() => {
          setIsOpen(!isOpen)
        }}
        alignItems='center'
        justifyContent='space-between'
        className='day-of-games'
        css={css`
          border-radius: 8px;
          background-color: ${colors.SECONDARY_400};
          padding: 8px 16px;
          margin-bottom: 16px;
          cursor: pointer;
          transition: filter 0.2s ease-in-out;
          &:hover {
            filter: brightness(1.2);
          }
          svg {
            transition: transform 0.1s ease-in-out;
            ${!isOpen && 'transform: rotate(180deg);'}
          }
        `}
      >
        <Text variant='captionSmall' color={colors.NEUTRAL_0} weight='semiBold'>
          {date}
        </Text>
        <DropdownArrowIcon color={colors.NEUTRAL_0} />
      </Flex>
      {isOpen &&
        games.map((game, idx) => {
          if (isMobile) {
            return (
              <MobileGameListRow
                key={game.id}
                hasNextSibling={games.length > 1 && idx !== games.length - 1}
                game={game}
                gameStatusTypes={gameStatusTypes}
                gameTypes={gameTypes}
                isSoccer={isSoccer}
                isHockey={isHockey}
                scheduleId={scheduleId}
                isMySchedule={isMySchedule}
              />
            )
          }

          return (
            <GameListRowV2
              key={game.id}
              hasNextSibling={games.length > 1 && idx !== games.length - 1}
              game={game}
              gameStatusTypes={gameStatusTypes}
              gameTypes={gameTypes}
              isSoccer={isSoccer}
              isHockey={isHockey}
              isFirstGame={idx === 0}
              onClickFirstGame={onClickFirstGame}
              scheduleId={scheduleId}
              isMySchedule={isMySchedule}
            />
          )
        })}
    </div>
  )
}

const GameList = ({
  team_id,
  schedule_id,
  gameStatusTypes,
  gameTypes,
  slug: _slug = '/games',
  refreshKey,
  setVisibleGameIds,
  areFiltersHidden,
  setFilterForExporting = () => {},
  isSoccer = false,
  isHockey = false,
  onClickFirstGame = () => {},
  // this is only for team filtering
  scheduleId = null,
}) => {
  const {
    currentParams,
    teams,
    team,
    setTeam,
    filters,
    dateRange,
    setDateRange,
    state,
    setState,
    sort,
    setSort,
    loaded,
    // Venue/Facility filter values
    venues,
    venue,
    setVenue,
    facility,
    setFacility,
    loadingVenues,
  } = useGetScheduleFiltering(
    {
      team_id,
      schedule_id,
    },
    {
      autoSelectMostRecentComp: true,
      onScheduleChange: (id) => {
        setSlug(`/schedules/${id}/games`)
      },
    }
  )

  const [error, setError] = useState(false)
  const [loading, setLoading] = useState(true)
  const [items, setItems] = useState([])
  const [pagination, setPagination] = useState({})
  const [isAppending, setIsAppending] = useState(false)
  const isAppendingRef = useRef(false)
  const itemsRef = useRef([])

  useEffect(() => {
    itemsRef.current = items
  }, [items])

  const [slug, setSlug] = useState(
    currentParams?.sn_division
      ? `/schedules/${currentParams.sn_division}/games`
      : currentParams?.sn_schedule
      ? `/schedules/${currentParams.sn_schedule}/games`
      : _slug
  )
  const { setParam } = useUrlManager()

  const [timePeriod, setTimePeriod] = useState(
    currentParams?.sn_time_period || 'TODAY'
  )
  const hasCustomDateRange = !!(dateRange?.starts_at || dateRange?.ends_at)

  const getDateRangeForTimePeriod = (period) => {
    const now = dayjs()
    switch (period) {
      case 'TODAY':
        return {
          starts_at: now.startOf('day').format('YYYY-MM-DD'),
          ends_at: now.endOf('day').format('YYYY-MM-DD'),
        }
      case 'PAST':
        return {
          starts_at: now
            .subtract(2, 'year')
            .startOf('day')
            .format('YYYY-MM-DD'),
          ends_at: now
            .subtract(1, 'day')
            .endOf('day')
            .format('YYYY-MM-DD'),
        }
      case 'UPCOMING':
      default:
        return {
          starts_at: now.startOf('day').format('YYYY-MM-DD'),
          ends_at: now.add(1, 'year').endOf('day').format('YYYY-MM-DD'),
        }
    }
  }

  const getEffectiveDateRange = useCallback(() => {
    return hasCustomDateRange
      ? dateRange
      : getDateRangeForTimePeriod(timePeriod)
  }, [dateRange, hasCustomDateRange, timePeriod])

  const handleTimePeriodChange = (period) => {
    setTimePeriod(period)
    setParam('sn_time_period', period)
    if (period === 'PAST') {
      setSort('desc')
      setParam('sn_sort', 'desc')
    } else {
      setSort('asc')
      setParam('sn_sort', 'asc')
    }
  }

  const getItems = useCallback(
    async (page = 1, append = false) => {
      try {
        if (append) {
          setIsAppending(true)
          isAppendingRef.current = true
        } else {
          setLoading(true)
        }

        const query = { page, order: sort, exclude_cancelled_games: 1 }
        if (team_id || team) {
          query.team_id = team_id || team
        }
        // Add venue/facility filters
        if (venue) {
          query.venue_id = venue
        }
        if (facility) {
          query.facility_id = facility
        }
        const effectiveDateRange = getEffectiveDateRange()
        if (effectiveDateRange?.starts_at) {
          query.starts_after = dayjs(effectiveDateRange.starts_at)
            .hour(0)
            .minute(0)
            .second(0)
            .format()
        }
        if (effectiveDateRange?.ends_at) {
          query.starts_before = dayjs(effectiveDateRange.ends_at)
            .hour(0)
            .minute(0)
            .second(0)
            .add(1, 'day')
            .format()
        }
        if (
          !effectiveDateRange?.starts_at &&
          !effectiveDateRange?.ends_at &&
          !currentParams.sn_sort
        ) {
          query.default = 1
        }

        // Only update filter for exporting on initial load, not during append
        // This prevents unnecessary parent state updates that cause scroll reset
        if (!append) {
          // Extract schedule ID from current slug for export
          const scheduleIdMatch = slug.match(/\/schedules\/([^/]+)\/games/)
          const selectedScheduleId = scheduleIdMatch ? scheduleIdMatch[1] : null

          // Build export query excluding time-period dates (Past/Today/Upcoming).
          // Only include date params when the user explicitly set a custom date range.
          const { starts_after, starts_before, ...exportQuery } = query
          if (hasCustomDateRange) {
            exportQuery.starts_after = starts_after
            exportQuery.starts_before = starts_before
          }

          setFilterForExporting({
            ...exportQuery,
            _exportScheduleId: selectedScheduleId,
          })
        }
        const response = await req(slug, { query })

        if (append) {
          setItems((prevItems) => {
            const newItems = response.data.filter(
              (newItem) =>
                !prevItems.some((prevItem) => prevItem.id === newItem.id)
            )
            return [...prevItems, ...newItems]
          })
        } else {
          setItems(response.data)
        }

        if (typeof setVisibleGameIds === 'function') {
          if (append) {
            setVisibleGameIds((prevIds) => {
              const newIds = response.data
                .map((d) => d.id)
                .filter((id) => !prevIds.includes(id))
              return [...prevIds, ...newIds]
            })
          } else {
            setVisibleGameIds(response.data.map((d) => d.id))
          }
        }
        setPagination(response.meta.pagination)
      } catch (e) {
        setError(e?.message)
      } finally {
        setLoading(false)
        setIsAppending(false)
        isAppendingRef.current = false
      }
    },
    [
      sort,
      team_id,
      team,
      currentParams,
      slug,
      setFilterForExporting,
      setVisibleGameIds,
      venue,
      facility,
      getEffectiveDateRange,
      hasCustomDateRange,
    ]
  )

  const hasMore = pagination && pagination.current_page < pagination.total_pages

  const loadMoreGames = useCallback(() => {
    if (loading || isAppendingRef.current || !hasMore) {
      return
    }
    const nextPage = (pagination.current_page || 0) + 1
    getItems(nextPage, true)
  }, [loading, hasMore, pagination.current_page, getItems])

  const handleScroll = useCallback(() => {
    const { scrollTop, scrollHeight, clientHeight } = document.documentElement
    const nearBottom =
      scrollHeight - (scrollTop + clientHeight) < SCROLL_THRESHOLD
    if (nearBottom && hasMore && !loading && !isAppendingRef.current) {
      loadMoreGames()
    }
  }, [hasMore, loading, loadMoreGames])

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [handleScroll])

  useEffect(() => {
    if (
      !loaded ||
      currentParams?.sn_sort ||
      hasCustomDateRange ||
      timePeriod !== 'PAST' ||
      sort === 'desc'
    ) {
      return
    }
    setSort('desc')
    setParam('sn_sort', 'desc')
  }, [
    currentParams?.sn_sort,
    hasCustomDateRange,
    loaded,
    setParam,
    setSort,
    sort,
    timePeriod,
  ])

  useEffect(() => {
    if (!loaded) {
      return
    }
    if (
      !currentParams?.sn_sort &&
      !hasCustomDateRange &&
      timePeriod === 'PAST' &&
      sort !== 'desc'
    ) {
      return
    }
    getItems(1, false)
  }, [
    currentParams?.sn_sort,
    dateRange.ends_at,
    dateRange.starts_at,
    getItems,
    hasCustomDateRange,
    loaded,
    refreshKey,
    slug,
    sort,
    team,
    timePeriod,
    venue,
    facility,
  ])

  let dates = {}
  items.forEach((item) => {
    const day = dayjs(item.starts_at).format('dddd, MMM D, YYYY')
    dates[day] = dates[day] || []
    dates[day].push(item)
  })

  return (
    <div
      css={css`
        margin-top: 24px;
        margin-bottom: 84px;
      `}
    >
      <GameListFilters
        filters={filters}
        dateRange={dateRange}
        team_id={team_id}
        onTeamChange={setTeam}
        onScheduleChange={(newScheduleId) => {
          if (newScheduleId) {
            setSlug(`/schedules/${newScheduleId}/games`)
          } else {
            setSlug(_slug)
          }
        }}
        setDateRange={setDateRange}
        hasCustomDateRange={hasCustomDateRange}
        state={state}
        setState={setState}
        sort={sort}
        setSort={setSort}
        teams={teams}
        team={team}
        loading={loading}
        useDateRange
        isHidden={areFiltersHidden}
        // Venue/Facility filter props
        venues={venues}
        venue={venue}
        onVenueChange={setVenue}
        facility={facility}
        onFacilityChange={setFacility}
        loadingVenues={loadingVenues}
        useVenueFilter
      >
        <div
          css={css`
            display: flex;
            justify-content: space-between;
            align-items: center;
            width: 100%;
            gap: 16px;
            ${media.mobile} {
              flex-direction: column;
              align-items: stretch;
            }
          `}
        >
          <TimePeriodFilter
            selected={timePeriod}
            onSelect={handleTimePeriodChange}
            disabled={loading || hasCustomDateRange}
          />
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 8px;
              ${media.mobile} {
                justify-content: space-between;
              }
            `}
          >
            <div
              css={css`
                display: flex;
                align-items: center;
                gap: 0px;
                border: 1px solid ${colors.SECONDARY_200};
                border-radius: 8px;
                height: 40px;
                min-width: 180px;
                opacity: ${loading ? 0.5 : 1};
                transition: opacity 0.2s ease;
                ${media.mobile} {
                  flex: 1;
                }
              `}
            >
              <button
                type='button'
                disabled={loading}
                onClick={() => {
                  setSort('asc')
                  setParam('sn_sort', 'asc')
                }}
                css={css`
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  flex: 1;
                  height: 40px;
                  border-radius: 8px 0 0 8px;
                  background-color: ${sort === 'asc'
                    ? colors.SECONDARY_300
                    : 'transparent'};
                  border: none;
                  cursor: ${loading ? 'not-allowed' : 'pointer'};
                  transition: all 0.2s ease;
                  font-size: 14px;
                  font-weight: ${sort === 'asc' ? 700 : 400};
                  line-height: 20.3px;
                  color: ${colors.NEUTRAL_0};

                  ${!loading &&
                  css`
                    &:hover {
                      background-color: ${sort === 'asc'
                        ? colors.SECONDARY_300
                        : colors.SECONDARY_200};
                    }
                    &:active {
                      transform: scale(0.98);
                    }
                  `}
                `}
              >
                Oldest
              </button>
              <button
                type='button'
                disabled={loading}
                onClick={() => {
                  setSort('desc')
                  setParam('sn_sort', 'desc')
                }}
                css={css`
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  flex: 1;
                  height: 40px;
                  border-radius: 0 8px 8px 0;
                  background-color: ${sort === 'desc'
                    ? colors.SECONDARY_300
                    : 'transparent'};
                  border: none;
                  cursor: ${loading ? 'not-allowed' : 'pointer'};
                  transition: all 0.2s ease;
                  font-size: 14px;
                  font-weight: ${sort === 'desc' ? 700 : 400};
                  line-height: 20.3px;
                  color: ${colors.NEUTRAL_0};

                  ${!loading &&
                  css`
                    &:hover {
                      background-color: ${sort === 'desc'
                        ? colors.SECONDARY_300
                        : colors.SECONDARY_200};
                    }
                    &:active {
                      transform: scale(0.98);
                    }
                  `}
                `}
              >
                Newest
              </button>
            </div>
          </div>
        </div>
      </GameListFilters>
      {loading || items.length === 0 ? (
        <ListStatusView
          loading={loading}
          zeroItems={items.length === 0}
          noItemsText='games'
          error={error}
        />
      ) : (
        <>
          <Mobile>
            {(isMobile) => {
              return Object.keys(dates).map((date) => {
                const listOfGames = dates[date]
                return (
                  <DayOfGames
                    key={date}
                    date={date}
                    games={listOfGames}
                    gameStatusTypes={gameStatusTypes}
                    gameTypes={gameTypes}
                    isMobile={isMobile}
                    isSoccer={isSoccer}
                    isHockey={isHockey}
                    onClickFirstGame={onClickFirstGame}
                    scheduleId={schedule_id || scheduleId}
                  />
                )
              })
            }}
          </Mobile>
          <div
            css={css`
              display: flex;
              justify-content: center;
              align-items: center;
              padding: 24px 0;
              min-height: 60px;
            `}
          >
            {isAppending && (
              <div
                css={css`
                  display: inline-flex;
                  align-items: center;
                  justify-content: center;
                  gap: 8px;
                `}
              >
                <Spin size='small' />
                <Text
                  variant='paragraphSmall'
                  weight='regular'
                  color={colors.NEUTRAL_50}
                  style='font-size: 14px; line-height: 1;'
                >
                  Loading More...
                </Text>
              </div>
            )}
            {!hasMore &&
              !loading &&
              items.length > 0 &&
              pagination &&
              pagination.total_pages > 1 && (
                <Text
                  variant='paragraphSmall'
                  weight='regular'
                  color={colors.NEUTRAL_50}
                  style='font-size: 14px;'
                >
                  No More Results
                </Text>
              )}
          </div>
        </>
      )}
      <FloatButton.BackTop
        icon={<ArrowUpOutlined />}
        css={css`
          .ant-float-btn-body {
            background-color: ${colors.PRIMARY} !important;
          }
          .ant-float-btn-icon {
            color: ${colors.NEUTRAL_0} !important;
          }
        `}
      />
    </div>
  )
}

const mapStateToProps = (state, { sportId }) => {
  return {
    gameTypes: getAllGameTypes(state),
    gameStatusTypes: getGameStatusTypesBySportId(state, sportId),
  }
}

export default connect(mapStateToProps)(GameList)
